import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { lazy, Suspense, useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { CartProvider } from "../lib/cart";
import { Deferred } from "../components/Deferred";
import { Toaster } from "../components/ui/sonner";

// Non-critical UI is code-split out of the initial bundle and mounted after
// the browser goes idle, so first paint, the LCP image and hydration win the
// main thread and network. Rendering is identical once mounted.
const CartDrawer = lazy(() => import("../components/CartDrawer").then((m) => ({ default: m.CartDrawer })));
const HealthIndicator = lazy(() => import("../components/HealthIndicator").then((m) => ({ default: m.HealthIndicator })));
const AnalyticsProvider = lazy(() =>
  import("../components/analytics/AnalyticsProvider").then((m) => ({ default: m.AnalyticsProvider }))
);
const CookieConsent = lazy(() =>
  import("../components/analytics/CookieConsent").then((m) => ({ default: m.CookieConsent }))
);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "N S Physiotherapy, Centurion Clinic & Mobile Physio" },
      { name: "description", content: "Expert physiotherapy in Centurion and at home. Back & neck, sports injuries, neuro rehab and medico legal services." },
      { name: "author", content: "N S Physiotherapy" },
      { property: "og:title", content: "N S Physiotherapy, Finding Paths to Relief" },
      { property: "og:description", content: "In clinic and mobile physiotherapy across Gauteng. Book online today." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "N S Physiotherapy" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "theme-color", content: "#1c3a4a" },
      {
        name: "google-site-verification",
        content: "yz5xdD7HKxhRoRaNUYTe-YNZTL-9xsD5CTXY6kQq63w",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.png?v=2", type: "image/png", sizes: "48x48" },
      { rel: "icon", href: "/favicon-192.png?v=2", type: "image/png", sizes: "192x192" },
      { rel: "icon", href: "/favicon-512.png?v=2", type: "image/png", sizes: "512x512" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png?v=2", sizes: "180x180" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
    ],
    scripts: [
      // Load web fonts without blocking first paint. The print media trick
      // downloads the stylesheet asynchronously, then flips it to "all".
      {
        children: `(function(){var l=document.createElement('link');l.rel='stylesheet';l.href='https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700&display=swap';l.media='print';l.onload=function(){l.media='all'};document.head.appendChild(l);})();`,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["MedicalBusiness", "Physiotherapy", "LocalBusiness"],
          "@id": "https://nsphysiotherapy.co.za/#business",
          name: "N S Physiotherapy",
          alternateName: ["NS Physiotherapy", "N.S. Physiotherapy Centurion"],
          description:
            "Physiotherapist in Centurion offering in clinic and home visit physiotherapy for back pain, neck pain, sports injuries, post operative rehab and neurological rehabilitation.",
          url: "https://nsphysiotherapy.co.za",
          telephone: "+27502157368",
          email: "info@nsphysiotherapy.co.za",
          priceRange: "R600-R1050",
          currenciesAccepted: "ZAR",
          paymentAccepted: "Cash, Card, EFT, Medical Aid",
          medicalSpecialty: "Physiotherapy",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Shop 11, Highveld, Eco Boulevard Shopping Centre, 270 Witch Hazel Avenue",
            addressLocality: "Centurion",
            addressRegion: "Gauteng",
            postalCode: "0157",
            addressCountry: "ZA",
          },
          geo: { "@type": "GeoCoordinates", latitude: -25.8664, longitude: 28.1839 },
          hasMap: "https://maps.google.com/maps?cid=3658509947226059915",
          areaServed: [
            { "@type": "City", name: "Centurion" },
            { "@type": "City", name: "Midrand" },
            { "@type": "Place", name: "Eco Park" },
            { "@type": "Place", name: "Eco Boulevard" },
            { "@type": "Place", name: "Highveld" },
            { "@type": "Place", name: "Die Hoewes" },
            { "@type": "Place", name: "Irene" },
            { "@type": "AdministrativeArea", name: "Gauteng" },
          ],
          availableService: [
            "Back pain physiotherapy",
            "Neck pain physiotherapy",
            "Sports injury physiotherapy",
            "Post operative rehabilitation",
            "Stroke rehabilitation",
            "Home visit physiotherapy",
            "Dry needling",
            "Orthopaedic rehabilitation",
            "Medico legal reports",
          ],
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              opens: "07:00",
              closes: "20:00",
            },
          ],
          sameAs: [
            "https://www.facebook.com/605667692627058",
            "https://www.instagram.com/nsphysiotherapy",
            "https://maps.google.com/maps?cid=3658509947226059915",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://nsphysiotherapy.co.za/#organization",
          name: "N S Physiotherapy",
          url: "https://nsphysiotherapy.co.za",
          logo: "https://nsphysiotherapy.co.za/favicon-512.png?v=2",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          url: "https://nsphysiotherapy.co.za",
          name: "N S Physiotherapy",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://nsphysiotherapy.co.za/blog?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <noscript
          dangerouslySetInnerHTML={{
            __html: `<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700&display=swap">`,
          }}
        />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <CartProvider>
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
        <Toaster richColors position="top-center" />
        <Deferred>
          <Suspense fallback={null}>
            <CartDrawer />
            <HealthIndicator />
            <AnalyticsProvider />
            <CookieConsent />
          </Suspense>
        </Deferred>
      </CartProvider>
    </QueryClientProvider>
  );
}
